import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "hh_portal_token";
export const USER_KEY = "hh_portal_user";

const api = axios.create({ baseURL: API_BASE });

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredAuth(token, user, remember = true) {
  const store = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  store.setItem(TOKEN_KEY, token);
  store.setItem(USER_KEY, JSON.stringify(user));
  other.removeItem(TOKEN_KEY);
  other.removeItem(USER_KEY);
}

export function clearStoredAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/login")) {
      clearStoredAuth();
      window.dispatchEvent(new Event("hh-unauthorized"));
    }
    return Promise.reject(error);
  }
);

export function extractError(error, fallback = "Something went wrong. Please try again.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => d.msg || d).join(", ");
  }
  if (error?.message === "Network Error") return "Network error \u2014 please check your connection.";
  return fallback;
}

/**
 * Download an authenticated file (PDF/ZIP) via the backend proxy.
 * `upstreamUrl` is the upstream relative path (e.g. /api/jobs/{id}/wtn.pdf)
 * OR pass zip=true to fetch the documents zip.
 */
export async function downloadFile({ upstreamUrl, zip = false, filename = "document", path = null, params = null }) {
  let reqPath;
  let reqParams;
  if (path) {
    reqPath = path;
    reqParams = params || {};
  } else if (zip) {
    reqPath = "/portal/documents/zip";
    reqParams = {};
  } else {
    reqPath = "/download";
    reqParams = { url: upstreamUrl, filename };
  }
  const response = await api.get(reqPath, { params: reqParams, responseType: "blob" });

  // Try to read filename from content-disposition
  let outName = filename;
  const cd = response.headers?.["content-disposition"];
  if (cd) {
    const match = /filename=\"?([^\";]+)\"?/i.exec(cd);
    if (match && match[1]) outName = match[1];
  }

  const blob = new Blob([response.data], { type: response.data.type || "application/octet-stream" });
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = outName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default api;
